import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, Phone } from "lucide-react";

const WHATSAPP_NUMBER = "919270425109";

const plans = [
  {
    name: "Basic Caregiving",
    subtitle: "Patients & Elderly Care",
    theme: "pink",
    prices: [
      { label: "10 Hours", amount: "1,000" },
      { label: "24 Hours", amount: "1,500" },
    ],
    features: [
      "Mobility Assistance",
      "Daily Living Assistance",
      "Meal Feeding",
      "Medication Reminder",
      "Patient/Elder's Room Housekeeping",
      "Companionship",
    ],
    popular: false,
  },
  {
    name: "Personal Caregiving",
    subtitle: "Patients & Elderly Care",
    theme: "teal",
    prices: [
      { label: "10 Hours", amount: "1,200" },
      { label: "24 Hours", amount: "1,500" },
    ],
    features: [
      "Personal Hygiene and Grooming",
      "Diaper Changer",
      "Meal Feeding Assistance",
      "Medication Reminder",
      "Toileting Support",
      "Patient/Elder's Room Housekeeping",
      "Companionship",
    ],
    popular: true,
  },
];

export default function PatientsElderlyCare() {
  return (
    <div className="pt-24 pb-20 bg-gradient-to-b from-[#F0FDFA] to-white min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#14B8A6] font-body mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-sm font-semibold text-[#14B8A6] uppercase tracking-wider font-body">Services</span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight mt-3">
              Patients & Elderly Care
            </h1>
            <p className="text-base md:text-lg text-[#64748B] mt-4 font-body">
              Compassionate daily assistance for your elderly loved ones with dignity and respect. 
              Our verified caregivers ensure professional care in the comfort of your home.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]"
          >
            <img 
              src="/images/patient_care.png" 
              alt="Patients and Elderly Care" 
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan, idx) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.15, duration: 0.4 }}
              data-testid={`plan-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative rounded-3xl p-8 border ${
                plan.theme === "pink"
                  ? "border-brand-secondary/20 bg-gradient-to-b from-brand-secondary-light to-white shadow-xl shadow-brand-secondary/5"
                  : plan.popular
                    ? "border-[#14B8A6]/30 bg-gradient-to-b from-[#F0FDFA] to-white shadow-xl shadow-teal-500/5"
                    : "border-slate-100 bg-white shadow-sm"
              }`}
            >
              {plan.popular && (
                <span className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-white text-xs font-semibold font-body ${
                  plan.theme === "pink" ? "bg-brand-secondary" : "bg-[#14B8A6]"
                }`}>
                  Popular
                </span>
              )}
              <h3 className="font-heading text-xl font-bold text-[#0F172A] mb-1">{plan.name}</h3>
              <p className="text-sm text-[#64748B] font-body mb-6">{plan.subtitle}</p>

              <div className="flex gap-4 mb-6">
                {plan.prices.map((p) => (
                  <div key={p.label} className="flex-1 bg-slate-50 rounded-2xl p-4 text-center">
                    <p className="text-xs text-[#64748B] font-body mb-1">{p.label}</p>
                    <p className="font-heading text-2xl font-bold text-[#0F172A]">&#8377;{p.amount}</p>
                  </div>
                ))}
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      plan.theme === "pink" ? "text-brand-secondary" : "text-[#14B8A6]"
                    }`} strokeWidth={2} />
                    <span className="text-sm text-[#475569] font-body">{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi%2C%20I%20am%20interested%20in%20${encodeURIComponent(plan.name)}%20for%20Patients%20%26%20Elderly%20Care`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`plan-cta-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`w-full inline-flex items-center justify-center gap-2 h-12 rounded-full font-semibold font-body transition-all ${
                  plan.theme === "pink"
                    ? "bg-brand-secondary text-white hover:bg-[#fb5d77] shadow-lg shadow-brand-secondary/20"
                    : plan.popular
                      ? "bg-[#14B8A6] text-white hover:bg-[#0D9488] shadow-lg shadow-teal-500/20"
                      : "bg-slate-100 text-[#0F172A] hover:bg-slate-200"
                }`}
              >
                Enquire Now
              </a>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-[#64748B] font-body italic mb-4">
            * Prices may vary depending on condition &amp; duration.
          </p>
          <a
            href={`tel:+919270425109`}
            className="inline-flex items-center gap-2 text-[#14B8A6] font-semibold font-body hover:underline"
          >
            <Phone className="w-4 h-4" /> Call for custom requirements: +91 9270425109
          </a>
        </div>
      </div>
    </div>
  );
}
